import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ContextSource, ContextVersion } from "@bitacorapro/api-types";
import { ContextSourceUploadDrawer } from "../components/ContextSourceUploadDrawer";
import { EmptyState } from "../components/EmptyState";
import { QueryBoundary } from "../components/QueryBoundary";
import { SkelTable } from "../components/skeleton/SkelTable";
import {
  failureReasonLabel,
  kindLabel,
  statusLabel,
  statusTone,
} from "../features/context/contextUpload";
import { ApiError } from "../lib/api/error";
import {
  useContextSources,
  useContextVersions,
  useMyOrganization,
  useRecipe,
} from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import {
  canManageContextSources,
  isContextLayerEnabled,
  selectContextBrands,
} from "../lib/capabilities";
import { fmtDateTime } from "../lib/util";

// /contexto — the client's documents per brand and the versions the brand
// runs (ADR-0173 fase 2, PR-6). Org-scoped roles pick the brand and
// upload; a brand manager sees its own brand, read-only. The screen is
// reachable only while the org's `context_ingest` flag is on (nav gate +
// the empty state below for a direct URL).

export function ContextSourceStatusPill({ source }: { source: ContextSource }) {
  const tone = statusTone(source.status);
  return (
    <span className={`st-pill st-pill--${tone}`}>
      <span className={`st-dot st-dot--${tone}`} />
      {statusLabel(source.status)}
    </span>
  );
}

function validityLabel(s: ContextSource): string {
  if (!s.validFrom && !s.validTo) return "Hasta ser reemplazado";
  const from = s.validFrom ? fmtDateTime(s.validFrom) : "…";
  const to = s.validTo ? fmtDateTime(s.validTo) : "…";
  return `${from} – ${to}`;
}

export function ContextoMarca() {
  const session = useSession();
  const role = session?.organization.role;
  const org = useMyOrganization();
  const { data: recipe } = useRecipe();
  const brands = useMemo(() => selectContextBrands(org.data), [org.data]);
  const [brandId, setBrandId] = useState<string>("");
  const effectiveBrandId = brandId || brands[0]?.id || "";
  const enabled = isContextLayerEnabled(org.data);
  const canManage = canManageContextSources(role);

  const sources = useContextSources(effectiveBrandId, enabled && effectiveBrandId !== "");
  const versions = useContextVersions(effectiveBrandId, enabled && effectiveBrandId !== "");
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const brandName = brands.find((b) => b.id === effectiveBrandId)?.name ?? "";

  if (org.data && !enabled) {
    return (
      <div className="page">
        <EmptyState
          title="El contexto por cliente no está habilitado para esta organización."
          description="Lo activa el equipo BitácoraPro una vez firmados la autorización del cliente y el acuerdo de tratamiento de datos."
        />
      </div>
    );
  }

  const errorTitle =
    sources.error instanceof ApiError ? sources.error.message : "No pudimos cargar los documentos.";

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            Contexto del cliente{brandName ? ` · ${brandName}` : ""}
          </div>
          <h1 className="page-header__title">Documentos y reglas</h1>
          <p className="page-header__sub">
            Los estándares del cliente (política, checklist) se convierten en reglas que la IA usa
            al evaluar cada visita. Sube el documento, revisa las reglas y activa la versión.
          </p>
        </div>
        <div className="page-header__actions">
          {brands.length > 1 && (
            <label className="field">
              <span className="field__label">Marca</span>
              <select
                aria-label="Marca"
                value={effectiveBrandId}
                onChange={(e) => setBrandId(e.target.value)}
              >
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {canManage && effectiveBrandId && (
            <button type="button" className="btn btn--primary" onClick={() => setUploading(true)}>
              Subir documento
            </button>
          )}
        </div>
      </div>

      <div className="stack-gap">
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Documentos</div>
          </div>
          <QueryBoundary
            query={sources}
            label="los documentos del cliente"
            errorTitle={errorTitle}
            kind="section"
            skeleton={
              <SkelTable
                rows={3}
                cols={5}
                headers={["Documento", "Tipo", "Estado", "Vigencia", "Subido"]}
              />
            }
            isEmpty={(d) => d.sources.length === 0}
            empty={
              <EmptyState
                title="Aún no hay documentos para esta marca."
                description={
                  canManage
                    ? "Sube la política de exhibición o el checklist del cliente para que la IA extraiga sus reglas."
                    : "Cuando el equipo de la organización suba un documento, aparecerá aquí."
                }
              />
            }
          >
            {(data) => (
              <div className="panel__body--flush">
                <table className="dtable">
                  <thead>
                    <tr>
                      <th>Documento</th>
                      <th>Tipo</th>
                      <th>Estado</th>
                      <th>Vigencia</th>
                      <th>Subido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sources.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <Link to={`/contexto/fuentes/${s.id}`} className="cell-strong">
                            {s.fileName ?? kindLabel(recipe, s.kind)}
                          </Link>
                        </td>
                        <td>{kindLabel(recipe, s.kind)}</td>
                        <td>
                          <ContextSourceStatusPill source={s} />
                          {s.status === "failed" && s.failureReason && (
                            <div className="cell-meta">{failureReasonLabel(s.failureReason)}</div>
                          )}
                        </td>
                        <td className="cell-meta">{validityLabel(s)}</td>
                        <td className="cell-meta">{fmtDateTime(s.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </QueryBoundary>
        </div>

        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Versiones de contexto</div>
          </div>
          <QueryBoundary
            query={versions}
            label="las versiones"
            errorTitle="No pudimos cargar las versiones."
            kind="section"
            skeleton={
              <SkelTable rows={2} cols={4} headers={["Versión", "Estado", "Reglas", "Activada"]} />
            }
            isEmpty={(d) => d.versions.length === 0}
            empty={
              <EmptyState
                title="Esta marca todavía no tiene una versión activa."
                description="Las actas nuevas se evaluarán sin reglas del cliente hasta que actives un documento revisado."
              />
            }
          >
            {(data) => <VersionsTable versions={data.versions} />}
          </QueryBoundary>
        </div>
      </div>

      {uploading && effectiveBrandId && (
        <ContextSourceUploadDrawer
          brandId={effectiveBrandId}
          recipe={recipe}
          onClose={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            setToast("Documento subido. La extracción de reglas está en curso.");
            void sources.refetch();
          }}
        />
      )}

      {toast && (
        <div className="toast-zone">
          <div className="toast toast--ok" role="status" aria-live="polite">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

function VersionsTable({ versions }: { versions: ContextVersion[] }) {
  return (
    <div className="panel__body--flush">
      <table className="dtable">
        <thead>
          <tr>
            <th>Versión</th>
            <th>Estado</th>
            <th>Reglas</th>
            <th>Activada</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id}>
              <td>
                <span className="cell-strong">{v.label ?? v.contentSha256.slice(0, 12)}</span>
                <div className="cell-meta">
                  {v.sourceIds.length} documento{v.sourceIds.length === 1 ? "" : "s"}
                </div>
              </td>
              <td>
                <span className={`st-pill st-pill--${v.status === "active" ? "ok" : "idle"}`}>
                  <span className={`st-dot st-dot--${v.status === "active" ? "ok" : "idle"}`} />
                  {v.status === "active" ? "Activa" : "Archivada"}
                </span>
              </td>
              <td className="cell-meta">{v.ruleCount}</td>
              <td className="cell-meta">
                {fmtDateTime(v.activatedAt)}
                {v.supersedesVersionId && (
                  <>
                    {" · "}
                    <Link to={`/contexto/versiones/${v.id}/diff?against=${v.supersedesVersionId}`}>
                      qué cambió
                    </Link>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
